import { parseCsv } from "@/lib/safety/events";

/** Compass approaches in clockwise order */
export const DIRECTIONS = ["north", "east", "south", "west"];
export const DIRECTION_LABELS = { north: "North", east: "East", south: "South", west: "West" };
export const TURNS = ["left", "through", "right", "uturn"];
export const TURN_LABELS = { left: "Left", through: "Through", right: "Right", uturn: "U-Turn" };

export const INTERSECTION_ID_PROPERTY = "intersection_id";
export const DAY_PROPERTY = "dow";
export const TOTAL_PROPERTY = "total";

const normalizeDirection = (value) => String(value ?? "").trim().toLowerCase();

/**
 * Classify a movement by the approach it enters from and the leg it exits to.
 * A vehicle entering from the east is travelling west, so east -> north is a right turn.
 * @param {String} from approach direction (where the vehicle comes from)
 * @param {String} to   exit direction (where the vehicle goes to)
 * @return {String|null} left | through | right | uturn, or null for unknown directions
 */
export function classifyMovement(from, to) {
  const fromIndex = DIRECTIONS.indexOf(normalizeDirection(from));
  const toIndex = DIRECTIONS.indexOf(normalizeDirection(to));
  if (fromIndex === -1 || toIndex === -1) {
    return null;
  }
  const heading = (fromIndex + 2) % 4;
  switch ((toIndex - heading + 4) % 4) {
    case 0: return "through";
    case 1: return "right";
    case 3: return "left";
    default: return "uturn";
  }
}

/**
 * Parse a count that may use thousands separators or "-" for none.
 * @return {Number|null}
 */
export function parseCount(value) {
  const text = String(value ?? "").trim().replace(/,/g, "");
  if (text === "" || text === "-") {
    return null;
  }
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
}

/**
 * Parse the movements CSV (intersection_id, from_direction, to_direction, day_of_week, count).
 * Rows whose directions are not compass approaches are dropped and counted.
 * @param {String} csvText
 * @return {{ movements: Array<Object>, dropped: Number }}
 */
export function parseMovements(csvText) {
  const movements = [];
  let dropped = 0;
  for (const row of parseCsv(csvText)) {
    const intersectionId = String(row.intersection_id ?? "").trim();
    const from = normalizeDirection(row.from_direction);
    const to = normalizeDirection(row.to_direction);
    const dow = String(row.day_of_week ?? "").trim().toLowerCase();
    const turn = classifyMovement(from, to);
    if (!intersectionId || !dow || !turn) {
      dropped++;
      continue;
    }
    movements.push({ intersectionId, from, to, dow, turn, count: parseCount(row.count) ?? 0 });
  }
  return { movements, dropped };
}

/**
 * Summarize one intersection's movements for a day bucket.
 * @param {Array<Object>} movements rows for a single intersection and day
 * @return {{ total: Number, approaches: Object, matrix: Object }}
 *   approaches: { north: { total, left, through, right, uturn }, ... } (only approaches with rows)
 *   matrix: { [from]: { [to]: count } }
 */
export function summarizeMovements(movements) {
  const approaches = {};
  const matrix = {};
  let total = 0;
  for (const { from, to, turn, count } of movements) {
    if (!approaches[from]) {
      approaches[from] = { total: 0, left: 0, through: 0, right: 0, uturn: 0 };
    }
    approaches[from][turn] += count;
    approaches[from].total += count;
    matrix[from] = matrix[from] || {};
    matrix[from][to] = (matrix[from][to] || 0) + count;
    total += count;
  }
  return { total, approaches, matrix };
}

function describe(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const at = (p) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1))] : 0);
  return { max: sorted.length ? sorted[sorted.length - 1] : 0, p25: at(0.25), p50: at(0.5), p75: at(0.75) };
}

/**
 * Join the movement rows to the intersection points. One feature is emitted per
 * intersection per day bucket carrying its total so a single layer can be filtered by `dow`.
 * @param {Object} intersections GeoJSON FeatureCollection of Points with an intersection id property
 * @param {String} csvText       movements CSV contents
 * @param {Object} [options]
 * @param {String} [options.idProperty]   point property holding the intersection id (default: intersection_id)
 * @param {String} [options.nameProperty] point property holding a display name (default: name)
 * @param {Array<String>} [options.dayBuckets] day buckets to emit; defaults to those seen in the CSV
 * @return {{ geojson: Object, dayBuckets: Array<String>, summaries: Map, stats: Object }}
 *   summaries: Map<intersectionId, Map<dow, summary>> for the detail panel
 */
export function buildIntersectionGeoJSON(intersections, csvText, { idProperty = INTERSECTION_ID_PROPERTY, nameProperty = "name", dayBuckets } = {}) {
  const { movements, dropped } = parseMovements(csvText);
  const pointIds = new Set(intersections.features.map((f) => String(f.properties[idProperty])));
  const grouped = new Map();
  const seenBuckets = [];
  let unmatched = 0;
  for (const movement of movements) {
    if (!pointIds.has(movement.intersectionId)) {
      unmatched++;
      continue;
    }
    if (!seenBuckets.includes(movement.dow)) {
      seenBuckets.push(movement.dow);
    }
    if (!grouped.has(movement.intersectionId)) {
      grouped.set(movement.intersectionId, new Map());
    }
    const byDay = grouped.get(movement.intersectionId);
    if (!byDay.has(movement.dow)) {
      byDay.set(movement.dow, []);
    }
    byDay.get(movement.dow).push(movement);
  }

  const buckets = dayBuckets && dayBuckets.length ? dayBuckets.map((b) => String(b).toLowerCase()) : seenBuckets;
  const summaries = new Map();
  const features = [];
  const totals = [];
  let id = 0;
  for (const point of intersections.features) {
    const intersectionId = String(point.properties[idProperty]);
    const name = point.properties[nameProperty] || `Intersection ${intersectionId}`;
    const byDay = grouped.get(intersectionId) || new Map();
    const summaryByDay = new Map();
    for (const dow of buckets) {
      const summary = summarizeMovements(byDay.get(dow) || []);
      summaryByDay.set(dow, summary);
      if (summary.total > 0) {
        totals.push(summary.total);
      }
      features.push({
        type: "Feature",
        id: id++,
        geometry: point.geometry,
        properties: {
          ...point.properties,
          [INTERSECTION_ID_PROPERTY]: intersectionId,
          name,
          [DAY_PROPERTY]: dow,
          [TOTAL_PROPERTY]: summary.total
        }
      });
    }
    summaries.set(intersectionId, summaryByDay);
  }

  return {
    geojson: { type: "FeatureCollection", features },
    dayBuckets: buckets,
    summaries,
    stats: {
      rows: movements.length + dropped,
      dropped,
      unmatched,
      intersections: intersections.features.length,
      intersectionsWithData: grouped.size,
      total: describe(totals)
    }
  };
}
